import React, { FC } from 'react';
import { Alert, Col, Form, Row, Spin } from 'antd';
import { FormInstance } from 'antd/lib/form';
import {
  OptionProps,
  ReportKey,
  ReportsFilterOptionsResponse,
} from '@models/interfaces';
import { PrimaryButton, RangePicker, Select } from '@shared/modules';
import { isPaceTaskFilterSupported, SORT_ORDER_OPTIONS } from '../../models';

interface ReportDateFieldConfig {
  extra?: string;
  label: string;
  tooltip?: string;
}

const REPORT_DATE_FIELD_CONFIG: Partial<
  Record<ReportKey, ReportDateFieldConfig>
> = {
  [ReportKey.DatesAtAGlance]: {
    label: 'Milestone Date',
    tooltip:
      'Uses the actual date once a milestone is completed, otherwise the forecasted date.',
  },
  [ReportKey.OverdueTasks]: {
    label: 'Forecasted Date',
  },
  [ReportKey.NewClosedProjects]: {
    label: 'Report Period',
  },
  [ReportKey.ProjectDuration]: {
    label: 'Actual Completion Date',
    tooltip:
      "Filters only the project's end date — the start date (project assigned date) isn't filterable.",
  },
};

interface ReportsFiltersProps {
  currentGroupByOptions: OptionProps[];
  currentSortByOptions: OptionProps[];
  filterOptions?: ReportsFilterOptionsResponse | null;
  filterOptionsError: boolean;
  filterOptionsLoading: boolean;
  form: FormInstance;
  onApplyFilters: () => Promise<void>;
  onResetFilters: () => void;
  projectTypeOptions: OptionProps[];
  selectedReport: ReportKey;
}

export const ReportsFilters: FC<ReportsFiltersProps> = ({
  currentGroupByOptions,
  currentSortByOptions,
  filterOptions,
  filterOptionsError,
  filterOptionsLoading,
  form,
  onApplyFilters,
  onResetFilters,
  projectTypeOptions,
  selectedReport,
}: ReportsFiltersProps) => {
  const showPaceTaskFilters = isPaceTaskFilterSupported(selectedReport);
  const dateFieldConfig = REPORT_DATE_FIELD_CONFIG[selectedReport];

  return (
    <section className="prov-reports-page__filters">
      {filterOptionsError && (
        <Alert
          className="prov-reports-page__alert"
          message="Unable to load filter options"
          type="error"
        />
      )}
      <Spin spinning={filterOptionsLoading}>
        <Form form={form} layout="vertical" autoComplete="off">
          <Row gutter={16}>
            {!!dateFieldConfig && (
              <Col xl={6} md={8} sm={12} xs={24}>
                <RangePicker
                  className="prov-reports-page__date-range"
                  id="dateRange"
                  label={dateFieldConfig.label}
                  required={false}
                  formItemProps={{
                    extra: dateFieldConfig.extra,
                    tooltip: dateFieldConfig.tooltip,
                  }}
                />
              </Col>
            )}
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="projectType"
                label="Project Type"
                options={projectTypeOptions}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="projectSubtype"
                label="Project Subtype"
                options={filterOptions?.projectSubtypes ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="projectStatus"
                label="Project Status"
                options={filterOptions?.projectStatuses ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="progressStatus"
                label="Progress Status"
                options={filterOptions?.progressStatuses ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="state"
                label="State"
                options={filterOptions?.states ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="submarket"
                label="Submarket"
                options={filterOptions?.submarkets ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="towerOwner"
                label="Tower Owner"
                options={filterOptions?.towerOwners ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="providenceAgent"
                label="Providence Agent"
                options={filterOptions?.providenceAgents ?? []}
                elementProps={{ allowClear: true, mode: 'multiple' }}
              />
            </Col>
            {showPaceTaskFilters && (
              <>
                <Col xl={6} md={8} sm={12} xs={24}>
                  <Select
                    id="paceTaskScreen"
                    label="PACE Task Module"
                    options={filterOptions?.paceTaskScreens ?? []}
                    elementProps={{ allowClear: true, mode: 'multiple' }}
                  />
                </Col>
                <Col xl={6} md={8} sm={12} xs={24}>
                  <Select
                    id="paceTask"
                    label="PACE Task"
                    options={filterOptions?.paceTasks ?? []}
                    elementProps={{
                      allowClear: true,
                      mode: 'multiple',
                      showSearch: true,
                      filterOption: (input: string, option: any) =>
                        !!option &&
                        String(option.children)
                          .toUpperCase()
                          .indexOf(String(input).toUpperCase()) !== -1,
                    }}
                  />
                </Col>
              </>
            )}
            {!!currentGroupByOptions.length && (
              <Col xl={6} md={8} sm={12} xs={24}>
                <Select
                  id="groupBy"
                  label="Group By"
                  options={currentGroupByOptions}
                  elementProps={{ allowClear: true }}
                />
              </Col>
            )}
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="sortBy"
                label="Sort By"
                options={currentSortByOptions}
                elementProps={{ allowClear: true }}
              />
            </Col>
            <Col xl={6} md={8} sm={12} xs={24}>
              <Select
                id="sortOrder"
                label="Sort Order"
                options={SORT_ORDER_OPTIONS}
                elementProps={{ allowClear: true }}
              />
            </Col>
          </Row>
          <Row className="prov-reports-page__filter-actions">
            <PrimaryButton onClick={onApplyFilters} title="Apply" />
            <PrimaryButton
              onClick={onResetFilters}
              title="Reset"
              type="default"
            />
          </Row>
        </Form>
      </Spin>
    </section>
  );
};
